<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import axios from '@/axiosConfig';

// ===== Flow stages =====
// 1: enter code
// 2: enter phone
// 3: pick a day
// 4: pick a time
// 5: confirmation
const stage = ref(1);

// ===== Shared state =====
const codeDigits = ref(Array(8).fill(''));
const codeInputRefs = ref([]);
const scheduleCode = computed(() => codeDigits.value.join(''));
const phone = ref('');
const errorMessage = ref('');
const isLoading = ref(false);
const scheduleType = ref('battery');

const pageTitle = computed(() =>
  scheduleType.value === 'maintenance'
    ? 'Schedule Maintenance Work'
    : 'Schedule Door Lock Battery Replacement',
);

function handleCodeInput(index, event) {
  const val = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (val.length > 1) {
    // Handle paste: spread characters across boxes
    const chars = val.split('');
    for (let i = 0; i < chars.length && index + i < 8; i++) {
      codeDigits.value[index + i] = chars[i];
    }
    const nextIdx = Math.min(index + chars.length, 7);
    nextTick(() => codeInputRefs.value[nextIdx]?.focus());
  } else {
    codeDigits.value[index] = val;
    if (val && index < 7) {
      nextTick(() => codeInputRefs.value[index + 1]?.focus());
    }
  }
}

function handleCodeKeydown(index, event) {
  if (event.key === 'Backspace' && !codeDigits.value[index] && index > 0) {
    codeDigits.value[index - 1] = '';
    nextTick(() => codeInputRefs.value[index - 1]?.focus());
  }
  if (event.key === 'Enter') {
    validateCode();
  }
}

function handleCodePaste(event) {
  event.preventDefault();
  const paste = (event.clipboardData?.getData('text') || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);
  for (let i = 0; i < 8; i++) {
    codeDigits.value[i] = paste[i] || '';
  }
  const nextIdx = Math.min(paste.length, 7);
  nextTick(() => codeInputRefs.value[nextIdx]?.focus());
}

// Data returned from validation
const propertyAddress = ref('');
const propertyId = ref(null);
const fee = ref(0);
const tenantName = ref('');

// Available slots
const workDays = ref([]);
const selectedDay = ref(null);
const selectedBlock = ref(null);

// Confirmation
const showConfirmPopup = ref(false);
const bookingComplete = ref(false);
const appointmentDetails = ref(null);

// Pre-fill code and type from query params
onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const typeParam = urlParams.get('type');
  if (typeParam === 'maintenance') {
    scheduleType.value = 'maintenance';
  }
  const codeParam = urlParams.get('code');
  if (codeParam) {
    const clean = codeParam
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 8);
    for (let i = 0; i < 8; i++) {
      codeDigits.value[i] = clean[i] || '';
    }
  }
});

// ===== Stage 1: Validate code =====
async function validateCode() {
  errorMessage.value = '';
  if (!scheduleCode.value || scheduleCode.value.length !== 8) {
    errorMessage.value = 'Please enter a valid 8-character code.';
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await axios.post('/api/battery-tracker/validate-code', {
      code: scheduleCode.value.toUpperCase(),
    });
    propertyAddress.value = data.address;
    propertyId.value = data.propertyId;
    fee.value = data.fee;
    stage.value = 2;
  } catch (err) {
    errorMessage.value =
      err.response?.data?.error || 'Invalid or expired code.';
  } finally {
    isLoading.value = false;
  }
}

// ===== Stage 2: Validate phone =====
async function validatePhone() {
  errorMessage.value = '';
  const cleanPhone = phone.value.replace(/\D/g, '');
  if (cleanPhone.length !== 10) {
    errorMessage.value = 'Please enter a valid 10-digit phone number.';
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await axios.post('/api/battery-tracker/validate-phone', {
      code: scheduleCode.value.toUpperCase(),
      phone: cleanPhone,
    });
    tenantName.value = data.tenantName;
    // Load available slots
    await loadAvailableSlots();
    stage.value = 3;
  } catch (err) {
    errorMessage.value =
      err.response?.data?.error ||
      'Invalid code or phone, please contact communications@bigpropertymanagement.com';
  } finally {
    isLoading.value = false;
  }
}

// ===== Stage 3: Load available time slots =====
async function loadAvailableSlots() {
  const { data } = await axios.get('/api/battery-tracker/available-slots', {
    params: {
      code: scheduleCode.value.toUpperCase(),
      phone: phone.value.replace(/\D/g, ''),
    },
  });
  workDays.value = data.workDays;
}

function selectDay(day) {
  selectedDay.value = day;
  selectedBlock.value = null;
  stage.value = 4;
}

// ===== Stage 4: Select time block =====
function selectBlock(block) {
  if (!block.available) return;
  selectedBlock.value = block;
  showConfirmPopup.value = true;
}

// ===== Confirm and book =====
async function confirmBooking() {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    const { data } = await axios.post('/api/battery-tracker/book-appointment', {
      code: scheduleCode.value.toUpperCase(),
      phone: phone.value.replace(/\D/g, ''),
      date: selectedDay.value.date,
      startTime: selectedBlock.value.start,
      endTime: selectedBlock.value.end,
    });
    appointmentDetails.value = data.appointment;
    showConfirmPopup.value = false;
    bookingComplete.value = true;
    stage.value = 5;
  } catch (err) {
    errorMessage.value =
      err.response?.data?.error || 'Error booking appointment.';
    showConfirmPopup.value = false;
  } finally {
    isLoading.value = false;
  }
}

function cancelConfirm() {
  showConfirmPopup.value = false;
  selectedBlock.value = null;
}

function goBackToDay() {
  selectedBlock.value = null;
  stage.value = 3;
}

const feeDisplay = computed(() => {
  return fee.value ? `$${parseFloat(fee.value).toFixed(2)}` : '$0.00';
});

function formatTimeLabel(t) {
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const hr12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
  return `${hr12}:${m || '00'} ${ampm}`;
}
</script>

<template>
  <div class="schedule-page">
    <div class="schedule-header">
      <i
        class="fas header-icon"
        :class="
          scheduleType === 'maintenance' ? 'fa-wrench' : 'fa-calendar-check'
        "
      ></i>
      <h2>{{ pageTitle }}</h2>
    </div>

    <!-- Confirmation Popup -->
    <div v-if="showConfirmPopup" class="sched-overlay">
      <div class="sched-confirm-popup">
        <h3>Confirm Appointment</h3>
        <p><strong>Address:</strong> {{ propertyAddress }}</p>
        <p>
          <strong>Date:</strong> {{ selectedDay?.dayOfWeek }},
          {{ selectedDay?.month }}/{{ selectedDay?.dayNumber }}
        </p>
        <p><strong>Time:</strong> {{ selectedBlock?.label }}</p>
        <p><strong>Fee:</strong> {{ feeDisplay }}</p>
        <div class="sched-confirm-buttons">
          <button @click="cancelConfirm" class="sched-btn-cancel">
            Cancel
          </button>
          <button
            @click="confirmBooking"
            :disabled="isLoading"
            class="sched-btn-confirm"
          >
            {{ isLoading ? 'Booking...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Stage 1: Enter Code -->
    <div v-if="stage === 1" class="sched-stage">
      <p class="sched-instruction">
        Enter the 8-character code from your email:
      </p>
      <div class="sched-code-input-row">
        <input
          v-for="(_, i) in 8"
          :key="i"
          :ref="(el) => (codeInputRefs[i] = el)"
          :value="codeDigits[i]"
          type="text"
          maxlength="2"
          class="sched-code-box"
          @input="handleCodeInput(i, $event)"
          @keydown="handleCodeKeydown(i, $event)"
          @paste="handleCodePaste"
        />
      </div>
      <div v-if="errorMessage" class="sched-error">{{ errorMessage }}</div>
      <button
        @click="validateCode"
        :disabled="isLoading"
        class="sched-btn-primary"
      >
        {{ isLoading ? 'Verifying...' : 'Continue' }}
      </button>
    </div>

    <!-- Stage 2: Enter Phone -->
    <div v-if="stage === 2" class="sched-stage">
      <p class="sched-instruction">
        Enter your 10-digit phone number to verify your identity:
      </p>
      <input
        v-model="phone"
        type="tel"
        maxlength="10"
        class="sched-phone-input"
        placeholder="1234567890"
        @input="phone = phone.replace(/\D/g, '')"
        @keyup.enter="validatePhone"
      />
      <div v-if="errorMessage" class="sched-error">{{ errorMessage }}</div>
      <button
        @click="validatePhone"
        :disabled="isLoading"
        class="sched-btn-primary"
      >
        {{ isLoading ? 'Verifying...' : 'Continue' }}
      </button>
    </div>

    <!-- Stage 3: Pick a Day -->
    <div v-if="stage === 3" class="sched-stage">
      <div class="sched-fee-line">
        The fee for scheduling this service is
        <strong>{{ feeDisplay }}</strong>
      </div>
      <p class="sched-instruction">Select a day for your appointment:</p>
      <div class="sched-day-gallery">
        <div
          v-for="day in workDays"
          :key="day.date"
          class="sched-day-card"
          :class="{
            'sched-day-unavailable': !day.blocks.some((b) => b.available),
          }"
          @click="day.blocks.some((b) => b.available) && selectDay(day)"
        >
          <div class="sched-day-name">{{ day.dayOfWeek }}</div>
          <div class="sched-day-date">{{ day.month }}/{{ day.dayNumber }}</div>
        </div>
      </div>
      <div v-if="errorMessage" class="sched-error">{{ errorMessage }}</div>
    </div>

    <!-- Stage 4: Pick a Time -->
    <div v-if="stage === 4" class="sched-stage">
      <div class="sched-fee-line">
        The fee for scheduling this service is
        <strong>{{ feeDisplay }}</strong>
      </div>
      <p class="sched-instruction">
        {{ selectedDay?.dayOfWeek }}, {{ selectedDay?.month }}/{{
          selectedDay?.dayNumber
        }}
        — Select a time:
      </p>
      <div class="sched-time-list">
        <div
          v-for="block in selectedDay?.blocks"
          :key="block.start"
          class="sched-time-card"
          :class="{ 'sched-time-unavailable': !block.available }"
          @click="selectBlock(block)"
        >
          {{ block.label }}
          <span v-if="!block.available" class="sched-time-badge"
            >Unavailable</span
          >
        </div>
      </div>
      <button class="sched-btn-back" @click="goBackToDay">
        <i class="fas fa-arrow-left"></i> Back to days
      </button>
      <div v-if="errorMessage" class="sched-error">{{ errorMessage }}</div>
    </div>

    <!-- Stage 5: Booking Complete -->
    <div v-if="stage === 5" class="sched-stage sched-complete">
      <i class="fas fa-check-circle sched-complete-icon"></i>
      <h3>Appointment Confirmed!</h3>
      <div class="sched-complete-details">
        <p><strong>Address:</strong> {{ propertyAddress }}</p>
        <p><strong>Date:</strong> {{ appointmentDetails?.date }}</p>
        <p>
          <strong>Time:</strong>
          {{ formatTimeLabel(appointmentDetails?.startTime || '') }} -
          {{ formatTimeLabel(appointmentDetails?.endTime || '') }}
        </p>
        <p><strong>Fee:</strong> {{ feeDisplay }}</p>
      </div>
      <p class="sched-complete-note">
        A confirmation email has been sent. If you need to reschedule, please
        contact
        <strong>communications@bigpropertymanagement.com</strong>.
      </p>
    </div>
  </div>
</template>

<style scoped>
.schedule-page {
  max-width: 480px;
  margin: 0 auto;
  padding: 20px 16px;
  font-family: Arial, sans-serif;
  min-height: 100vh;
  background: #faf8fc;
}

.schedule-header {
  text-align: center;
  margin-bottom: 24px;
  color: #6e5875;
}

.header-icon {
  font-size: 2.5em;
  margin-bottom: 8px;
}

.schedule-header h2 {
  margin: 8px 0 0 0;
  font-size: 1.4em;
}

.sched-stage {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.sched-instruction {
  color: #555;
  margin-bottom: 16px;
  font-size: 1em;
}

.sched-code-input-row {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 16px;
}

.sched-code-box {
  width: 38px;
  height: 44px;
  font-family: 'Courier New', monospace;
  font-size: 1.3em;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  border: 2px solid #d4c8db;
  border-radius: 6px;
  outline: none;
  padding: 0;
  color: #333;
  background: #fff;
  caret-color: #6e5875;
}

.sched-code-box:focus {
  border-color: #6e5875;
  box-shadow: 0 0 0 2px rgba(110, 88, 117, 0.15);
}

.sched-phone-input {
  width: 90vw;
  max-width: 100%;
  padding: 14px;
  font-size: 1.3em;
  text-align: center;
  border: 2px solid #d4c8db;
  border-radius: 8px;
  outline: none;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.sched-phone-input:focus {
  border-color: #6e5875;
}

.sched-error {
  color: #d32f2f;
  margin: 12px 0;
  font-size: 0.9em;
  text-align: center;
}

.sched-btn-primary {
  display: block;
  width: 100%;
  padding: 14px;
  background: #6e5875;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1.1em;
  font-weight: 600;
  cursor: pointer;
}

.sched-btn-primary:disabled {
  background: #bbb;
  cursor: not-allowed;
}

.sched-btn-primary:active:not(:disabled) {
  background: #5a4862;
}

.sched-btn-back {
  display: inline-block;
  margin-top: 16px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #c4b5ce;
  border-radius: 6px;
  color: #6e5875;
  cursor: pointer;
  font-size: 0.9em;
}

.sched-fee-line {
  background: #f3eef6;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #555;
  font-size: 0.95em;
}

.sched-fee-line strong {
  color: #6e5875;
}

/* Day cards gallery */
.sched-day-gallery {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.sched-day-card {
  background: #fff;
  border: 2px solid #d4c8db;
  border-radius: 10px;
  padding: 12px 4px;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.15s,
    transform 0.1s;
}

.sched-day-card:active {
  transform: scale(0.96);
}

.sched-day-card:hover:not(.sched-day-unavailable) {
  border-color: #6e5875;
}

.sched-day-unavailable {
  opacity: 0.4;
  cursor: not-allowed;
}

.sched-day-name {
  font-size: 1.1em;
  font-weight: 700;
  color: #6e5875;
  margin-bottom: 4px;
}

.sched-day-date {
  font-size: 0.9em;
  color: #888;
}

/* Time block cards */
.sched-time-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sched-time-card {
  background: #fff;
  border: 2px solid #d4c8db;
  border-radius: 8px;
  padding: 14px 16px;
  font-size: 1em;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: border-color 0.15s;
}

.sched-time-card:hover:not(.sched-time-unavailable) {
  border-color: #6e5875;
}

.sched-time-unavailable {
  background: #f0f0f0;
  color: #aaa;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.sched-time-badge {
  font-size: 0.8em;
  color: #c97878;
}

/* Confirm popup */
.sched-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.sched-confirm-popup {
  background: #fff;
  padding: 28px;
  border-radius: 12px;
  max-width: 360px;
  width: 90%;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

.sched-confirm-popup h3 {
  margin-top: 0;
  color: #6e5875;
}

.sched-confirm-popup p {
  margin: 8px 0;
  color: #555;
}

.sched-confirm-buttons {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.sched-btn-cancel {
  flex: 1;
  padding: 12px;
  background: #eee;
  border: none;
  border-radius: 6px;
  color: #666;
  cursor: pointer;
  font-size: 1em;
}

.sched-btn-confirm {
  flex: 1;
  padding: 12px;
  background: #6e5875;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1em;
  font-weight: 600;
}

.sched-btn-confirm:disabled {
  background: #bbb;
}

/* Complete stage */
.sched-complete {
  text-align: center;
}

.sched-complete-icon {
  font-size: 4em;
  color: #28a745;
  margin-bottom: 12px;
}

.sched-complete h3 {
  color: #6e5875;
  margin-bottom: 16px;
}

.sched-complete-details {
  background: #f8f5fa;
  padding: 16px;
  border-radius: 8px;
  text-align: left;
  margin-bottom: 16px;
}

.sched-complete-details p {
  margin: 6px 0;
  color: #555;
}

.sched-complete-note {
  color: #888;
  font-size: 0.9em;
}
</style>
